import { useLanguage } from "../i18n/LanguageContext";
import { useMeta } from "../hooks/useMeta";
import { PageHeader } from "../components/PageHeader";
import { Overline, Reveal } from "../components/motion";
import { PButton } from "../components/forms";

export default function About() {
  const { t } = useLanguage();
  useMeta(t.seo.about[0], t.seo.about[1]);

  const images = [
    { src: "/images/products/detail-wood.jpg", caption: t.about.captions[0], cls: "md:col-span-5" },
    { src: "/images/products/detail-upholstery.jpg", caption: t.about.captions[1], cls: "md:col-span-7" },
    { src: "/images/products/studio-tower.jpg", caption: t.about.captions[2], cls: "md:col-span-7" },
    { src: "/images/products/cadillac-walnut-studio.jpg", caption: t.about.captions[3], cls: "md:col-span-5" },
  ];

  return (
    <div data-testid="about-page">
      <PageHeader id="about" overline={t.about.overline} lines={t.about.title} />

      <section className="mx-auto max-w-[1400px] px-6 pb-24 md:px-12 lg:px-20">
        <div className="grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-6">
            <p className="font-serif text-2xl font-light italic leading-relaxed text-[#1C1C1A] md:text-3xl">
              {t.about.p1}
            </p>
          </Reveal>
          <div className="flex flex-col gap-8 lg:col-span-4 lg:col-start-9">
            <Reveal delay={0.15}>
              <p className="text-base font-light leading-relaxed text-[#66625A]">{t.about.p2}</p>
            </Reveal>
            <Reveal delay={0.25}>
              <p className="text-base font-light leading-relaxed text-[#66625A]">{t.about.p3}</p>
            </Reveal>
          </div>
        </div>
      </section>

      <section data-testid="about-gallery" className="mx-auto max-w-[1400px] px-6 pb-28 md:px-12 md:pb-40 lg:px-20">
        <div className="grid gap-10 md:grid-cols-12 md:gap-x-12">
          {images.map((img, i) => (
            <Reveal key={img.src} delay={(i % 2) * 0.12} className={img.cls}>
              <figure className="group">
                <div className="overflow-hidden">
                  <img
                    src={img.src}
                    alt={img.caption}
                    className="aspect-[4/3] w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.04]"
                  />
                </div>
                <figcaption>
                  <Overline className="mt-4">{img.caption}</Overline>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-y border-[#1C1C1A]/10">
        <div className="mx-auto grid max-w-[1400px] gap-12 px-6 py-20 md:px-12 md:py-28 lg:grid-cols-12 lg:px-20">
          <div className="lg:col-span-4">
            <Reveal y={14}>
              <Overline className="mb-6">Unser Anspruch</Overline>
            </Reveal>
            <h2 className="font-serif text-4xl font-light leading-[1.02] tracking-tight text-[#1C1C1A] md:text-5xl">
              Weniger, aber besser.
            </h2>
          </div>
          <div className="grid gap-8 lg:col-span-7 lg:col-start-6 md:grid-cols-2">
            <Reveal delay={0.1}>
              <div className="border-t border-[#1C1C1A]/15 pt-6">
                <h3 className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#1C1C1A]">Material & Verarbeitung</h3>
                <p className="mt-4 text-sm font-light leading-relaxed text-[#66625A]">Massives Hainbuchenholz, sorgfältig ausgeführte Holzverbindungen und widerstandsfähige Polsterung bilden die Grundlage unserer Reformer.</p>
              </div>
            </Reveal>
            <Reveal delay={0.18}>
              <div className="border-t border-[#1C1C1A]/15 pt-6">
                <h3 className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#1C1C1A]">Persönliche Beratung</h3>
                <p className="mt-4 text-sm font-light leading-relaxed text-[#66625A]">Ob einzelner Reformer oder Studioausstattung: Wir beraten persönlich zu Modell, Edition und Bedarf und erstellen ein individuelles Angebot.</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section data-testid="about-principles" className="bg-[#F3EFEA]">
        <div className="mx-auto max-w-[1400px] px-6 py-24 md:px-12 md:py-32 lg:px-20">
          <Reveal y={14}>
            <Overline className="mb-12">{t.about.principlesOverline}</Overline>
          </Reveal>
          <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-5">
            {t.about.principles.map((pr, i) => (
              <Reveal key={pr.t} delay={i * 0.08}>
                <div data-testid={`about-principle-${i + 1}`} className="border-t border-[#1C1C1A]/15 pt-6">
                  <p className="font-serif text-4xl font-light italic text-[#C5A059]">{String(i + 1).padStart(2, "0")}</p>
                  <h3 className="mt-5 font-mono text-[11px] uppercase tracking-[0.3em] text-[#1C1C1A]">{pr.t}</h3>
                  <p className="mt-3 text-sm font-light leading-relaxed text-[#66625A]">{pr.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-16">
            <PButton to="/kontakt" variant="solid" data-testid="about-contact-cta">
              {t.about.cta}
            </PButton>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
